/**
 * Inbound Webhooks — admin page for wiring external lead-gen properties
 * (WordPress sites, Carrot, landing pages, etc.) into iSpeedToClose.
 *
 * Shows the operator's stable webhook URL + copy-paste WordPress setup
 * instructions, a kill switch, and a rolling 50-row activity log so they
 * can debug in real time.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, Check, Loader2, PowerOff, Power, RefreshCw, Radio, ExternalLink, Trash2, ChevronRight, Globe } from "lucide-react";

export default function AdminInboundWebhooks() {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [c, l] = await Promise.all([
        api.get("/inbound/webhook-config"),
        api.get("/inbound/webhook-log?limit=50"),
      ]);
      setCfg(c.data);
      setLog(l.data.log || []);
    } catch (err) {
      toast.error("Couldn't load webhook config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  // Auto-refresh log every 8s so new activity shows up while operator watches.
  useEffect(() => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/inbound/webhook-log?limit=50");
        setLog(data.log || []);
      } catch (_) { /* silent */ }
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const doCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(""), 1500);
    } catch (_) {
      toast.error("Clipboard copy failed");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Regenerate your webhook key? Any WordPress sites currently pointed at the old URL will stop working until you update them.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/inbound/webhook-config/regenerate");
      setCfg((c) => ({ ...c, webhook_key: data.webhook_key }));
      toast.success("Webhook key regenerated — update your sites now");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Regenerate failed");
    } finally { setBusy(false); }
  };

  const togglePause = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/inbound/webhook-config/toggle-pause", { paused: !cfg.paused });
      setCfg((c) => ({ ...c, paused: data.paused }));
      toast.success(data.paused ? "Inbound leads PAUSED — kill switch on" : "Inbound leads resumed");
    } catch (err) {
      toast.error("Toggle failed");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="p-6 flex items-center gap-2 text-[var(--ink-3)]"><Loader2 size={16} className="animate-spin" /> Loading…</div>;
  }

  const webhookUrl = `${window.location.origin}/api/inbound/lead/${cfg.webhook_key}`;
  const curlSnippet = `curl -X POST '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '{"property_address":"123 Main St","full_name":"Jane Doe","email":"jane@example.com","phone":"+12155551212","tcpa_consent":true,"source":"tpacquisitions"}'`;

  const phpSnippet = `add_action('wpforms_process_complete', function($fields, $entry, $form_data, $entry_id) {
    // Map your WPForm field IDs → the webhook keys iSpeedToClose expects.
    // Replace the "1"/"2"/"3"/"4" IDs with your form's actual field IDs
    // (visible under Fields → Advanced → Field ID in WPForms).
    $payload = [
        'property_address' => $fields[1]['value'] ?? '',
        'full_name'        => $fields[2]['value'] ?? '',
        'email'            => $fields[3]['value'] ?? '',
        'phone'            => $fields[4]['value'] ?? '',
        'tcpa_consent'     => true,
        'source'           => 'tpacquisitions', // or 'sellyourhomepa'
    ];
    wp_remote_post('${webhookUrl}', [
        'headers' => ['Content-Type' => 'application/json'],
        'body'    => wp_json_encode($payload),
        'timeout' => 8,
    ]);
}, 10, 4);`;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6" data-testid="admin-inbound-webhooks">
      <div>
        <h1 className="text-2xl font-black flex items-center gap-2">
          <Radio size={22} className="text-[var(--primary)]" /> Inbound Webhooks
        </h1>
        <p className="text-sm text-[var(--ink-3)] mt-1">
          Wire external lead-gen sites (WordPress, Carrot, landing pages) directly into your CRM. Every submission becomes a lead, fires your auto-response, and queues an Apex 2.0 call.
        </p>
      </div>

      {/* ── Kill switch */}
      <div className={`card border-2 ${cfg.paused ? "border-red-400 bg-red-50" : "border-emerald-300 bg-emerald-50/40"}`} data-testid="webhook-kill-switch">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 font-bold">
              {cfg.paused
                ? <><PowerOff size={16} className="text-red-600" /> Inbound webhooks are <span className="text-red-700">PAUSED</span></>
                : <><Power size={16} className="text-emerald-600" /> Inbound webhooks are <span className="text-emerald-700">LIVE</span></>}
            </div>
            <div className="text-xs text-[var(--ink-3)] mt-1">
              {cfg.paused
                ? "New submissions from your sites are being rejected. Toggle back on when you're ready."
                : "New submissions land as leads, fire SMS/email auto-response, and queue an Apex 2.0 call."}
            </div>
          </div>
          <button
            onClick={togglePause}
            disabled={busy}
            className={`px-4 py-2 rounded-lg font-bold text-sm ${cfg.paused ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}
            data-testid="webhook-toggle-pause"
          >
            {cfg.paused ? "Resume" : "Pause All"}
          </button>
        </div>
      </div>

      {/* ── Your webhook URL */}
      <div className="card space-y-3" data-testid="webhook-url-card">
        <div className="label-eyebrow flex items-center gap-1.5"><ExternalLink size={11} /> Your webhook URL</div>
        <div className="flex items-center gap-2">
          <code className="flex-1 bg-[var(--bg-2)] border border-[var(--line)] rounded-lg px-3 py-2 text-xs font-mono break-all" data-testid="webhook-url-value">
            {webhookUrl}
          </code>
          <button
            onClick={() => doCopy(webhookUrl, "url")}
            className="btn btn-secondary inline-flex items-center gap-1.5 shrink-0"
            data-testid="webhook-copy-url"
          >
            {copied === "url" ? <><Check size={14} className="text-emerald-600" /> Copied</> : <><Copy size={14} /> Copy URL</>}
          </button>
        </div>
        <button
          onClick={regenerate}
          disabled={busy}
          className="text-xs text-[var(--ink-3)] hover:text-red-600 inline-flex items-center gap-1"
          data-testid="webhook-regenerate"
        >
          <RefreshCw size={11} /> Regenerate key (invalidates old URL)
        </button>
      </div>

      {/* ── Sites we know how to brand */}
      {cfg.site_configs?.length > 0 && (
        <div className="card space-y-3" data-testid="webhook-sites-card">
          <div className="label-eyebrow flex items-center gap-1.5"><Globe size={11} /> Site-aware branding</div>
          <p className="text-xs text-[var(--ink-3)]">
            When a webhook payload includes <code className="text-[10px] bg-[var(--bg-2)] px-1 rounded">source</code> matching one of these keys, iSpeedToClose auto-selects the right brand name, phone, and Apex 2.0 script.
          </p>
          <div className="grid gap-2">
            {cfg.site_configs.map((s) => (
              <div key={s.key} className="flex items-center justify-between gap-3 bg-white border border-[var(--line)] rounded-lg px-3 py-2 text-sm">
                <div>
                  <div className="font-bold">{s.brand_name} <span className="text-[10px] text-[var(--ink-3)] font-mono bg-[var(--bg-2)] px-1.5 py-0.5 rounded ml-1">source={s.key}</span></div>
                  <div className="text-xs text-[var(--ink-3)] mt-0.5">{s.brand_domain} · {s.brand_phone} · {s.geo_label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── WordPress setup */}
      <div className="card space-y-4" data-testid="webhook-setup-card">
        <div className="label-eyebrow">WordPress setup — pick one</div>

        <details className="border border-[var(--line)] rounded-lg" open>
          <summary className="px-4 py-3 cursor-pointer font-bold text-sm flex items-center gap-2">
            <ChevronRight size={14} /> Option A — WPForms Pro (Marketing → Webhooks)
          </summary>
          <div className="px-4 pb-4 pt-2 text-sm space-y-2 text-[var(--ink-2)]">
            <ol className="list-decimal ml-5 space-y-1">
              <li>Open your form in WPForms Pro</li>
              <li>Go to <b>Marketing → Webhooks → Add New Webhook</b></li>
              <li>Request Method: <b>POST</b> · Request Format: <b>JSON</b></li>
              <li>Request URL: <button onClick={() => doCopy(webhookUrl, "urlB")} className="underline decoration-dotted">{webhookUrl}</button></li>
              <li>Add these request-body key mappings (drag from your form fields):
                <ul className="list-disc ml-5 mt-1 text-xs">
                  <li><code>property_address</code> → your Property Address field</li>
                  <li><code>full_name</code> → your Name field</li>
                  <li><code>email</code> → Email field</li>
                  <li><code>phone</code> → Phone field</li>
                  <li><code>tcpa_consent</code> → your consent checkbox (true)</li>
                  <li><code>source</code> → static value: <b>tpacquisitions</b> or <b>sellyourhomepa</b></li>
                </ul>
              </li>
              <li>Save → submit a test entry → watch the log below</li>
            </ol>
          </div>
        </details>

        <details className="border border-[var(--line)] rounded-lg">
          <summary className="px-4 py-3 cursor-pointer font-bold text-sm flex items-center gap-2">
            <ChevronRight size={14} /> Option B — PHP snippet (any WordPress form plugin)
          </summary>
          <div className="px-4 pb-4 pt-2 text-sm space-y-2 text-[var(--ink-2)]">
            <p>Paste this into your theme&apos;s <code>functions.php</code> or a Code Snippets plugin. Update the WPForm field IDs (visible in the WPForms editor → each field&apos;s Advanced tab).</p>
            <div className="relative">
              <pre className="bg-[#0F172A] text-emerald-300 text-[11px] font-mono p-3 rounded-lg overflow-x-auto" data-testid="webhook-php-snippet">{phpSnippet}</pre>
              <button
                onClick={() => doCopy(phpSnippet, "php")}
                className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold px-2 py-1 rounded inline-flex items-center gap-1"
              >
                {copied === "php" ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
              </button>
            </div>
          </div>
        </details>

        <details className="border border-[var(--line)] rounded-lg">
          <summary className="px-4 py-3 cursor-pointer font-bold text-sm flex items-center gap-2">
            <ChevronRight size={14} /> Option C — curl (test from your terminal)
          </summary>
          <div className="px-4 pb-4 pt-2 text-sm space-y-2 text-[var(--ink-2)]">
            <p>Run this in Terminal / iTerm to fire a test lead into your CRM without touching WordPress:</p>
            <div className="relative">
              <pre className="bg-[#0F172A] text-emerald-300 text-[11px] font-mono p-3 rounded-lg overflow-x-auto" data-testid="webhook-curl-snippet">{curlSnippet}</pre>
              <button
                onClick={() => doCopy(curlSnippet, "curl")}
                className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold px-2 py-1 rounded inline-flex items-center gap-1"
              >
                {copied === "curl" ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
              </button>
            </div>
          </div>
        </details>
      </div>

      {/* ── Activity log */}
      <div className="card space-y-3" data-testid="webhook-log-card">
        <div className="flex items-center justify-between">
          <div className="label-eyebrow">Recent activity ({log.length})</div>
          <div className="text-[10px] text-[var(--ink-3)]">Auto-refreshes every 8s</div>
        </div>
        {log.length === 0 ? (
          <div className="text-sm text-[var(--ink-3)] py-6 text-center">
            No inbound activity yet. Submit a test lead using Option C above, then watch it appear here.
          </div>
        ) : (
          <div className="grid gap-1 max-h-96 overflow-y-auto">
            {log.map((row) => (
              <div key={row.id} className="flex items-center gap-2 text-xs bg-white border border-[var(--line)] rounded-lg px-2.5 py-1.5">
                <StatusPill status={row.status} />
                <span className="font-mono text-[var(--ink-3)] shrink-0 min-w-[6.5rem]">{formatTime(row.at)}</span>
                <span className="font-bold text-[var(--ink)]">{row.source_key || "—"}</span>
                {row.lead_id && <span className="text-[10px] text-[var(--ink-3)] truncate">→ lead {row.lead_id.slice(0, 8)}…</span>}
                {row.note && <span className="text-[10px] text-red-700 truncate">· {row.note}</span>}
                <span className="ml-auto text-[10px] font-mono text-[var(--ink-3)] shrink-0">{row.ip}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const styles = {
    created: "bg-emerald-100 text-emerald-800",
    merged:  "bg-blue-100 text-blue-800",
    spam:    "bg-red-100 text-red-800",
    paused:  "bg-amber-100 text-amber-800",
    rejected:"bg-red-100 text-red-800",
  };
  return (
    <span className={`text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded ${styles[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

function formatTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch (_) { return iso; }
}
